import addHours from 'date-fns/addHours';
import roundToNearestMinutes from 'date-fns/roundToNearestMinutes';
import setHours from 'date-fns/setHours';
import isEqual from 'lodash/isEqual';

import { getTimezoneOffsetDiff, type RequestedResponder } from '@atlassian/eoc-schedule-common';

import { WEEK_DAY_COUNT } from '../../common/constants';
import {
	type DayTimeRange,
	type OptionField,
	type RotationBase,
	type RotationDetail,
	RotationType,
	ShiftFrequency,
	type TimingType,
} from '../../common/types';
import { type GetRotationResponse, type TimeTemplate } from '../../services/endpoints/edit/types';
import { formatting } from '../../utils/date';
import { isEmpty } from '../../utils/validators';
import { type RecipientUpdate } from '../edit-store/state/types';

import { MONDAY } from './constants';
import { type FormFields, HackStatus, type TemplateOption } from './types';

// TODO: Make more sofiscated
// Might be better if we use similar logic to clone schedule
// such as -> "Clone - [Rotation Name]" | "Rotation"
const generateName = (rotations: RotationBase[]) => {
	return ['Rotation', rotations.length + 1].join(' ');
};

const fromRotationFormToRotationDetail = ({
	base,
	rotationId,
}: {
	base: FormFields;
	rotationId: string | undefined;
}): RotationDetail => {
	const {
		startTime,
		restrictions,
		participants: responders,
		name,
		shiftPeriod,
		hasEndTime,
		template,
		shiftType,
		timingType,
		endTime,
	} = base;

	const state: RotationDetail = {
		id: rotationId,
		name,
		responders,
		restrictions,
		rotationType: shiftType.value,
		timingType,
		rotationPeriod: parseInt(shiftPeriod),
		startTimeRaw: formatting.toDateHourMinute(startTime),
	};

	if (template) {
		state.timeTemplateId = template.value;
	}

	if (hasEndTime && endTime) {
		state.endTimeRaw = formatting.toDateHourMinute(endTime);
	}

	return state;
};

const fromRotationDetailToRotationForm = ({
	base,
	templateOptions,
	shiftTypeOptions,
	shiftFrequencyOptions,
}: {
	base: RotationDetail;
	shiftFrequencyOptions: OptionField<ShiftFrequency>[];
	shiftTypeOptions: OptionField<RotationType>[];
	templateOptions: TemplateOption[];
}): FormFields => {
	const frequency = getShiftFrequency(base);

	const fields = {
		name: base.name,
		shiftType: shiftTypeOptions.find(({ value }) => value === base?.rotationType)!,
		shiftFrequency: shiftFrequencyOptions.find(({ value }) => value === frequency)!,
		startTime: formatting.toISO(base.startTimeRaw),
		restrictions: base.restrictions,
		timingType: base.timingType,
		participants: base.responders,
		shiftPeriod: base.rotationPeriod.toString(),
	} as FormFields;

	if (base.timeTemplateId) {
		fields.template = templateOptions.find(({ value }) => value === base.timeTemplateId);
	} else {
		fields.template = null;
	}

	if (base.endTimeRaw) {
		fields.endTime = formatting.toISO(base.endTimeRaw);
		fields.hasEndTime = true;
	} else {
		fields.hasEndTime = false;
	}

	// WARNING: Consider removing this when comparing RotationDetail and RotationForm
	fields.hackStatus = HackStatus.None;

	return fields;
};

const fromGetRotationResponseToRecipientUpdate = (
	response: GetRotationResponse,
): RecipientUpdate => ({
	responders: response.respondersShifted,
	startTime: formatting.toISO(response.startTimeForRecipientUpdate),
	shiftable: !isEqual(response.respondersShifted, response.responders),
});

const fromGetRotationResponseToRotationDetail = (
	response: GetRotationResponse,
	templates: TimeTemplate[],
): RotationDetail => {
	let restrictions: DayTimeRange[];
	let timingType: TimingType;

	const {
		id,
		name,
		responders,
		rotationType,
		rotationPeriod,
		startTimeRaw,
		endTimeRaw,
		timeTemplateId,
	} = response;

	if (timeTemplateId) {
		const template = templates.find((template) => timeTemplateId === template.id)!;

		restrictions = template.restrictions;
		timingType = template.timingType!;
	} else {
		restrictions = response.restrictions!;
		timingType = response.timingType!;
	}

	const detail: RotationDetail = {
		id,
		name,
		responders,
		rotationType,
		restrictions,
		rotationPeriod,
		timeTemplateId,
		timingType,
		startTimeRaw,
	};

	if (endTimeRaw) {
		detail.endTimeRaw = endTimeRaw;
	}

	return detail;
};

const downsizeRestriction = (intervals: DayTimeRange[]) => {
	const firstInterval = intervals[0]!;
	// WARNING: Current is not correct 'days', are missing
	return [
		{
			...firstInterval,
			startDay: null,
			endDay: null,
		},
	] as unknown as DayTimeRange[];
};

const expandRestriction = (intervals: DayTimeRange[]) => {
	const interval = intervals[0]!;
	return [
		{
			...interval,
			// NOTE: assuming default start day is Monday
			startDay: MONDAY,
			endDay: MONDAY,
		},
	];
};

const getShiftFrequency = (rotation?: RotationDetail) => {
	if (isEmpty(rotation)) {
		return undefined;
	}

	if (rotation.rotationType === RotationType.DAILY && rotation.rotationPeriod === 1) {
		return ShiftFrequency.Daily;
	}

	if (rotation.rotationType === RotationType.WEEKLY && rotation.rotationPeriod === 1) {
		return ShiftFrequency.Weekly;
	}

	return ShiftFrequency.Custom;
};

/**
 * Get 8:00 AM in given timezone
 * Imitates BE response (start epoch) for startTime
 * Ex. Browser: UTC +3:00, Incoming: UTC +1:00 -> UTC +3:00 6:00 AM
 * Ex. Browser: UTC +3:00, Incoming: UTC -9:00 -> UTC +3:00 8:00 PM
 * @return time - Date
 */
const generateDefaultStart = (timezone?: string | number) => {
	const time = new Date();

	time.setMinutes(0, 0, 0);

	const localToday8am = setHours(time, 8);

	const offsetHack = timezone ? getTimezoneOffsetDiff(timezone) : 0;

	const movedToday8am = new Date(localToday8am.valueOf() + offsetHack);

	return movedToday8am;
};

const generateDefaultEnd = (base?: number | Date | string) => {
	const time = base ? new Date(base) : new Date();

	time.setSeconds(0, 0);

	const nextHourUsingStartTime = roundToNearestMinutes(addHours(time, 1), {
		nearestTo: 30,
	});

	return nextHourUsingStartTime;
};

const getNextInterval = (base: DayTimeRange[]): DayTimeRange => {
	const last = { ...base[base.length - 1] };

	last.startDay = (last.startDay % WEEK_DAY_COUNT) + 1;
	last.endDay = (last.endDay % WEEK_DAY_COUNT) + 1;

	return last;
};

const rearrageParticipants = (
	base: RequestedResponder<true, true>[],
	shifted: RequestedResponder<true, true>[],
) => {
	const getIndexOfItemOnRespondersShifted = (item: RequestedResponder<true, true>) =>
		shifted.findIndex(({ id }) => item.id === id);

	const rearraged = [...base].sort((r1, r2) =>
		getIndexOfItemOnRespondersShifted(r1) === -1 || getIndexOfItemOnRespondersShifted(r2) === -1
			? 0
			: getIndexOfItemOnRespondersShifted(r1) - getIndexOfItemOnRespondersShifted(r2),
	);

	return rearraged;
};

export {
	downsizeRestriction,
	expandRestriction,
	fromGetRotationResponseToRotationDetail,
	fromRotationDetailToRotationForm,
	fromRotationFormToRotationDetail,
	fromGetRotationResponseToRecipientUpdate,
	generateDefaultEnd,
	rearrageParticipants,
	generateDefaultStart,
	generateName,
	getNextInterval,
	getShiftFrequency,
};
